import { LinkIdContainer, LINK_ENTITY_TYPE } from 'owa-attachment-link-common';
import { closeLinkContextMenu, removeComposeLinkViewState } from 'owa-compose-link';
import { isHTMLAnchorElement } from 'owa-cross-window-types';
import type EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import { lazyGetLinkProviderType, LinkProviderType } from 'owa-fileprovider-link';
import { AnchorElementsSource, getNumberOfSharingLinks, RecipientContainer } from 'owa-link-data';
import type RecipientWellWithFindControlViewState from 'owa-recipient-types/lib/types/RecipientWellWithFindControlViewState';
import { trace } from 'owa-trace';
import { getEntitySelector } from 'roosterjs-editor-dom';
import {
    ChangeSource,
    ContentChangedEvent,
    EditorPlugin,
    EntityOperation,
    EntityOperationEvent,
    IEditor,
    PluginEvent,
    PluginEventType,
    PositionType,
} from 'roosterjs-editor-types';
import getAllSharingLinks from '../utils/getAllSharingLinks';
import handleBeforePasteEvent from '../utils/handleBeforePasteEvent';
import { handleInsertLinkShortcut } from '../utils/handleInsertLinkShortcut';
import { getSharingInfoToTryUpdateLink } from '../utils/handleOneLink/getSharingInfoToTryUpdateLink';
import isLinkRemovalRelatedEvent from '../utils/isLinkRemovalRelatedEvent';
import isReadonlyBeautifulLink from '../utils/isReadonlyBeautifulLink';
import { isSupportedProviderType } from '../utils/isSupportedProviderType';
import { processAllLinksInEditor } from '../utils/processAllLinksInEditor';
import processContentChangedEvent from '../utils/processContentChangedEvent';
import { processKeyDownEvent } from '../utils/processKeyDownEvent';
import { removeComposeOnlyContent } from '../utils/removeComposeOnlyContent';
import trySetIdAndClassName from '../utils/trySetIdAndClassName';
import { SMART_COMPOSE_TEXT_PREDICTION } from '../utils/constants';
import { removeReactContent } from '../utils/removeReactContent';

const RESIZE_EVENT_NAME = 'resize';

export default class LinkPlugin implements EditorPlugin {
    public editor: IEditor;
    private pendingSPLinksTask;
    private onCutDisposer: () => void;

    // This is for handling stuck spinner from Ctrl+Z undo
    // If we can't find the link Id in this array, we would remove the spinner on event contentChange from setContent.
    private linkIdsWithSpinner: string[];

    constructor(
        public editorViewState: EditorViewState,
        private linkIdContainer: LinkIdContainer,
        public getRecipientWells: () => RecipientContainer[],
        public fromAddress: string,
        public isCalendar: boolean,
        public allowCtrlK: boolean = false
    ) {
        this.linkIdsWithSpinner = [];
    }

    public getName() {
        return 'Link';
    }

    public initialize(editor: IEditor): void {
        trace.info('LinkPlugin.initialize');
        this.editor = editor;
        this.onCutDisposer = this.editor.addDomEventHandler('cut', this.tryGetAllSPLinks);
        window.addEventListener(RESIZE_EVENT_NAME, this.onScroll);
        if (this.allowCtrlK) {
            const wells = this.getRecipientWells();
            const insertLinksHost = {
                toRecipientWell: <RecipientWellWithFindControlViewState>wells[0],
                ccRecipientWell: <RecipientWellWithFindControlViewState>wells[1],
                bccRecipientWell: <RecipientWellWithFindControlViewState>wells[2],
                attachmentWell: this.linkIdContainer,
            };

            this.editor.addContentEditFeature(
                handleInsertLinkShortcut(
                    this.editor,
                    this.editorViewState,
                    this.fromAddress,
                    insertLinksHost
                )
            );
        }
    }

    public async onPluginEvent(event: PluginEvent): Promise<void> {
        if (event.eventType === PluginEventType.EntityOperation) {
            if (event.entity.type === LINK_ENTITY_TYPE) {
                this.onEntityEvent(event);
            }
        }
        if (event.eventType === PluginEventType.Scroll) {
            this.onScroll();
        } else if (event.eventType === PluginEventType.BeforePaste) {
            // Handles pasting text on the editor (but not html blob)
            handleBeforePasteEvent(
                event,
                this,
                this.linkIdContainer,
                this.linkIdsWithSpinner,
                this.editor.getDocument().defaultView,
                this.isCalendar
            );
        } else if (event.eventType === PluginEventType.EditorReady) {
            trace.info('LinkPlugin.EditorReady');
            processAllLinksInEditor(
                this,
                this.linkIdContainer,
                this.linkIdsWithSpinner,
                false /* shouldChangeLinkId */,
                AnchorElementsSource.EditorLoadedOrDraftOrReply,
                this.editor.getDocument().defaultView,
                this.isCalendar
            );
        } else if (event.eventType === PluginEventType.ContentChanged) {
            // Handles adding an hyperlink using the hyperlink button in compose or pasting html blob
            const contentChangedEvent: ContentChangedEvent = event as ContentChangedEvent;
            if (
                (contentChangedEvent.source === ChangeSource.AutoLink ||
                    contentChangedEvent.source === ChangeSource.CreateLink ||
                    contentChangedEvent.source === SMART_COMPOSE_TEXT_PREDICTION) &&
                contentChangedEvent.data &&
                isHTMLAnchorElement(contentChangedEvent.data)
            ) {
                const getLinkProviderType = await lazyGetLinkProviderType.import();
                const providerType: LinkProviderType = await getLinkProviderType(
                    contentChangedEvent.data.href
                );
                if (!isSupportedProviderType(providerType)) {
                    return;
                }

                let anchorElementsSource: AnchorElementsSource = AnchorElementsSource.Unknown;
                if (
                    contentChangedEvent.source === ChangeSource.AutoLink ||
                    contentChangedEvent.source === SMART_COMPOSE_TEXT_PREDICTION
                ) {
                    anchorElementsSource = AnchorElementsSource.AutoLink;
                } else if (contentChangedEvent.source === ChangeSource.CreateLink) {
                    anchorElementsSource = AnchorElementsSource.InsertLinkFromToolbar;
                }
                trace.info('LinkPlugin.handleLink');
                trySetIdAndClassName(
                    contentChangedEvent.data,
                    AnchorElementsSource.AutoLink,
                    true /*shouldChangeLinkId*/
                );
                getSharingInfoToTryUpdateLink(
                    this,
                    providerType,
                    contentChangedEvent.source === ChangeSource.AutoLink ||
                        contentChangedEvent.source ===
                            SMART_COMPOSE_TEXT_PREDICTION /* shouldBeautifyLink */,
                    true /* shouldRehydrate  */,
                    false /* isPreviouslyBeautified */,
                    contentChangedEvent.data,
                    this.linkIdContainer,
                    0 /* linkIndex */,
                    this.linkIdsWithSpinner,
                    anchorElementsSource,
                    this.editor.getDocument().defaultView,
                    this.isCalendar
                );
            } else {
                // Handles loading from draft, reply, reply all and fwd, undo
                processContentChangedEvent(
                    event,
                    this,
                    this.linkIdContainer,
                    this.linkIdsWithSpinner,
                    this.editor.getDocument().defaultView,
                    this.isCalendar
                );
            }
        } else if (event.eventType === PluginEventType.ExtractContentWithDom) {
            trace.info('LinkPlugin.ExtractContentWithDom');
            removeComposeOnlyContent(event.clonedRoot);
            return;
        } else if (
            event.eventType === PluginEventType.KeyUp ||
            event.eventType === PluginEventType.MouseUp
        ) {
            const selectionNode = this.editor.getSelectionRange();
            if (selectionNode) {
                const startNode: Node = selectionNode.startContainer;
                const endNode: Node = selectionNode.endContainer;

                if (startNode === endNode && startNode.nodeName.toLowerCase() === 'a') {
                    const anchor = startNode as HTMLAnchorElement;
                    if (
                        isReadonlyBeautifulLink(anchor) &&
                        selectionNode.startOffset === selectionNode.endOffset
                    ) {
                        // Sometimes cursor is trapped inside the readonly beautiful link, where edit won't work.
                        // So we have to move it outside the readonly link for edit to work
                        if (selectionNode.startOffset !== startNode.childNodes.length) {
                            trace.info('move select before the anchor element.');
                            this.editor.select(anchor, PositionType.Before);
                        } else {
                            trace.info('move select after the anchor element.');
                            this.editor.select(anchor, PositionType.After);
                        }
                    }
                }
            }
        } else if (event.eventType === PluginEventType.KeyDown) {
            processKeyDownEvent(event, this.editor);
        } else if (event.eventType === PluginEventType.BeforeDispose) {
            const selector = getEntitySelector(LINK_ENTITY_TYPE);
            const beautifulLinkEntites = this.editor.queryElements(selector);
            beautifulLinkEntites.forEach(entity => {
                removeReactContent(entity);
            });
        }

        this.processLinkRemovalRelatedEvents(event);
    }

    private onEntityEvent(event: EntityOperationEvent) {
        switch (event.operation) {
            case EntityOperation.RemoveFromStart:
            case EntityOperation.RemoveFromEnd:
            case EntityOperation.Overwrite:
                this.editor.addUndoSnapshot(() => {
                    const { wrapper } = event.entity;
                    this.editor.deleteNode(wrapper);
                    if (event.rawEvent) {
                        event.rawEvent.preventDefault();
                        event.rawEvent.stopPropagation();
                    }
                    removeReactContent(wrapper);
                });
                break;
            case EntityOperation.ReplaceTemporaryContent:
                // We remove the Entity element from around the link, as it has
                // style: inline-block, which can cause UI issues for the recipient.
                event.entity.wrapper.parentNode.replaceChild(
                    event.entity.wrapper.firstChild,
                    event.entity.wrapper
                );
                break;
        }
    }

    private processLinkRemovalRelatedEvents(event: PluginEvent): void {
        if (isLinkRemovalRelatedEvent(event, this.editor)) {
            this.tryGetAllSPLinks();
        }
    }

    private tryGetAllSPLinks = () => {
        if (!this.pendingSPLinksTask) {
            this.pendingSPLinksTask = setTimeout(() => {
                this.getAllSPLinks();
            }, 500);
        }
    };

    private getAllSPLinks(): void {
        this.pendingSPLinksTask = null;
        getAllSharingLinks(this.editor, this.linkIdContainer);
    }

    private onScroll = () => {
        if (getNumberOfSharingLinks() > 0) {
            closeLinkContextMenu();
        }
    };

    public dispose(): void {
        trace.info('LinkPlugin.dispose');
        if (this.onCutDisposer) {
            this.onCutDisposer();
            this.onCutDisposer = null;
        }

        if (this.pendingSPLinksTask) {
            clearTimeout(this.pendingSPLinksTask);
            this.pendingSPLinksTask = null;
        }

        this.editor = null;
        window.removeEventListener(RESIZE_EVENT_NAME, this.onScroll);

        if (getNumberOfSharingLinks() > 0) {
            closeLinkContextMenu();

            const linkIds = this.linkIdContainer?.sharingLinkIds;
            if (linkIds?.length > 0) {
                linkIds.forEach(linkId => {
                    removeComposeLinkViewState(linkId);
                });
            }
        }
    }
}
